import { MarkupError } from "./errors.js";
import type { ElementDefinition } from "./registry.js";
import type { XmlAttribute, XmlElement } from "./xml.js";
import { parseMarkup } from "./xml.js";

/**
 * A set of attribute defaults, matched by element type or by class name.
 *
 * Deliberately the smallest thing that removes real repetition: `<Style for="Button" width="7em"/>`
 * says every button is that wide, and `<Style class="hint" color="textDisabled"/>` names a look
 * an element opts into. No selectors, no triggers, no templates — the theme already owns how a
 * control looks, and a style here only sets the attributes someone would otherwise repeat.
 */
export class StyleSheet {
  #byType = new Map<string, XmlAttribute[]>();
  #byClass = new Map<string, XmlAttribute[]>();

  /**
   * Attributes that apply to an element, least specific first.
   *
   * Type first, then each class in the order the element listed them, so the element's own
   * attributes — applied last by the caller — always win. Ordering by the element's list rather
   * than by declaration order means `class="hint danger"` reads the way it looks.
   */
  attributesFor(definition: ElementDefinition<never>, classes: readonly string[]): XmlAttribute[] {
    const out: XmlAttribute[] = [];
    for (const key of [definition.tag, ...(definition.aliases ?? [])]) {
      const byType = this.#byType.get(key);
      if (byType) out.push(...byType);
    }
    for (const name of classes) {
      const byClass = this.#byClass.get(name);
      if (byClass) out.push(...byClass);
    }
    return out;
  }

  get empty(): boolean {
    return this.#byType.size === 0 && this.#byClass.size === 0;
  }

  /** Read one `<Styles>` element's children into this sheet. */
  addFrom(styles: XmlElement): void {
    for (const child of styles.children) {
      if (child.name !== "Style") {
        throw new MarkupError(`<Styles> holds <Style> elements, not <${child.name}>`, child);
      }
      const forType = child.attributes.find((a) => a.name === "for");
      const forClass = child.attributes.find((a) => a.name === "class");
      if (!forType && !forClass) {
        throw new MarkupError(`<Style> needs a "for" or a "class" to say what it applies to`, child);
      }
      const rest = child.attributes.filter((a) => a.name !== "for" && a.name !== "class");
      if (forType) append(this.#byType, forType.value, rest);
      for (const name of (forClass?.value ?? "").split(/\s+/).filter(Boolean)) {
        append(this.#byClass, name, rest);
      }
    }
  }
}

function append(map: Map<string, XmlAttribute[]>, key: string, attributes: XmlAttribute[]): void {
  const existing = map.get(key);
  if (existing) existing.push(...attributes);
  else map.set(key, [...attributes]);
}

/**
 * Read a standalone stylesheet document.
 *
 * Separate from a layout so one sheet can be shared by several — a set of windows that should
 * look alike is the case that makes `<Styles>` inside each of them a copy-paste problem.
 */
export function loadStyles(xml: string): StyleSheet {
  const root = parseMarkup(xml);
  if (root.name !== "Styles") {
    throw new MarkupError(`a stylesheet's root element is <Styles>, not <${root.name}>`, root);
  }
  const sheet = new StyleSheet();
  sheet.addFrom(root);
  return sheet;
}

/** Split `<Styles>` out of an element's children, folding it into a sheet. */
export function takeStyles(root: XmlElement, into: StyleSheet): StyleSheet {
  const kept: XmlElement[] = [];
  for (const child of root.children) {
    if (child.name === "Styles") into.addFrom(child);
    else kept.push(child);
  }
  root.children = kept;
  return into;
}

export function classesOf(element: XmlElement): string[] {
  const value = element.attributes.find((a) => a.name === "class")?.value ?? "";
  return value.split(/\s+/).filter(Boolean);
}
